/* eslint-disable eqeqeq */
import { Injectable } from '@angular/core';
import { checkIsNAN, formatNumber, roundToNearestValue } from '../../../../shared/utils';
import {
  DpFactorType,
  FHAFactorMatrix,
  FundingFeeType,
  LoanInfoRequest,
  LoanInfoRequestGvtFundingFactors,
  LoanInfoResponse,
  LoanInfoResponse2,
  LtvCltvHcltvResponse,
  MortgageType,
  RHSFactorMatrix,
  SubordinateFinanceModel,
} from '../../../models/loan-information.model';
import { Decimal } from 'decimal.js';

@Injectable({
  providedIn: 'root',
})
/**
 * This service was direct copy of Clear 1 code.
 * It has not been optimized.
 * TODO need to confirm file is thoroughly checked for correct calculations
 * TODO maybe this just needs to all be in a utility file
 */
export class LoanInfoService {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor() {}

  // Main Calculate Function
  calculateLoan = (loanInfo: LoanInfoRequest): LoanInfoResponse => {
    let firstRatio = 0;
    let secondRatio = 0;
    const isRefinance: boolean = loanInfo.loanPurpose == '2';
    if (loanInfo.mipffFinancingCalcOption === null || loanInfo.mipffFinancingCalcOption === 0) {
      loanInfo.upfrontMiType = 'financed';
    } else {
      loanInfo.upfrontMiType = 'paidInCash';
    }
    const response = new LoanInfoResponse();
    let ltvresponse = new LtvCltvHcltvResponse();
    const baseLoan = roundToNearestValue(
      this._calculateBaseLoanAmount(
        loanInfo.downPaymentPercent,
        loanInfo.downPayment,
        loanInfo.salesPrice,
        loanInfo.appraisalValue,
        loanInfo.dpFactor,
        loanInfo.baseLoan,
      ),
    );
    const upfrontMiFactor = this._calculateUpfrontMiFactor(
      isRefinance,
      loanInfo.mortgageType,
      loanInfo.fundingFeeType,
      loanInfo.loanProgramCode,
      loanInfo.salesPrice,
      baseLoan,
      loanInfo.upfrontMiFactor,
      loanInfo.fhaCaseNoAssigned,
    );
    const upfrontMiAmount = this._calculateUpfrontAmount(upfrontMiFactor, baseLoan, loanInfo.mortgageType);
    const mipPaidInCash = this._calculateMIPaidInCash(loanInfo.upfrontMiType, upfrontMiAmount);
    const totalloanamount = this._calculateNoteLoanAmount(loanInfo.upfrontMiType, loanInfo.mortgageType, baseLoan, upfrontMiAmount, mipPaidInCash);
    ltvresponse = this._calculateLTVCLTVHCLTV(
      loanInfo.mortgageType,
      baseLoan,
      loanInfo.appraisalValue,
      loanInfo.salesPrice,
      loanInfo.subordinateFinanceAmount,
      totalloanamount,
      loanInfo.loanPurpose,
      loanInfo.hellocCreditLimit,
    );
    const firstMortgagePI = formatNumber(
      this._calculatePI(totalloanamount, loanInfo.interestRate, loanInfo.loanTerm, !loanInfo.loanTerm || loanInfo.loanTerm <= 0),
      2,
    );
    const monthlyMiFactor = this._calculateMonthlyMiFactor(
      isRefinance,
      loanInfo.mortgageType,
      ltvresponse.ltv,
      baseLoan,
      loanInfo.loanTerm,
      loanInfo.fhaCaseNoAssigned,
      loanInfo.monthlyMiFactor,
      loanInfo.loanProgramCode,
    );
    const monthlyMiAmount = formatNumber(
      this._calculateMIAmount(
        firstMortgagePI,
        totalloanamount,
        loanInfo.interestRate,
        monthlyMiFactor,
        upfrontMiFactor,
        loanInfo.mortgageType,
        baseLoan,
      ),
      2,
    );
    loanInfo.proposedCombinedHousingExpenses = formatNumber(
      checkIsNAN(loanInfo.proposedCombinedHousingExpenses) + checkIsNAN(firstMortgagePI) + checkIsNAN(monthlyMiAmount),
      2,
    );
    if (loanInfo.occupancyType == 2) {
      loanInfo.PresentCombinedHousingExpenses = formatNumber(loanInfo.PresentCombinedHousingExpenses, 2);
    }

    if (loanInfo.grossMonthlyIncome > 0) {
      if (loanInfo.occupancyType === 1) {
        if (loanInfo.loanPurpose == '1' && (loanInfo.mortgageType == 2 || loanInfo.mortgageType == 4)) {
          firstRatio = formatNumber(
            this._calculateFirstRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.grossMonthlyIncome, loanInfo.monthlyMCC),
            2,
          );
          secondRatio = formatNumber(
            this._calculateSecondRatio(
              loanInfo.proposedCombinedHousingExpenses,
              loanInfo.totalMonthlyPayment,
              loanInfo.grossMonthlyIncome,
              loanInfo.monthlyMCC,
            ),
            2,
          );
        } else {
          firstRatio = formatNumber(this._calculateFirstRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.grossMonthlyIncome, 0), 2);
          secondRatio = formatNumber(
            this._calculateSecondRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.totalMonthlyPayment, loanInfo.grossMonthlyIncome, 0),
            2,
          );
        }
      } else if (loanInfo.occupancyType === 2) {
        firstRatio = formatNumber(this._calculateFirstRatio(loanInfo.PresentCombinedHousingExpenses, loanInfo.grossMonthlyIncome, 0), 2);
        let PresentAndProposedCombinedHousingExpenses = 0;
        PresentAndProposedCombinedHousingExpenses = loanInfo.PresentCombinedHousingExpenses + loanInfo.proposedCombinedHousingExpenses;
        secondRatio = formatNumber(
          this._calculateSecondRatio(PresentAndProposedCombinedHousingExpenses, loanInfo.totalMonthlyPayment, loanInfo.grossMonthlyIncome, 0),
          2,
        );
      } else {
        firstRatio = formatNumber(this._calculateFirstRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.grossMonthlyIncome, 0), 2);
        secondRatio = formatNumber(
          this._calculateSecondRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.totalMonthlyPayment, loanInfo.grossMonthlyIncome, 0),
          2,
        );
      }
    }

    response.baseLoan = baseLoan;
    response.mipPaidInCash = mipPaidInCash;
    response.upfrontMiFactor = upfrontMiFactor;
    response.upfrontMiAmount = upfrontMiAmount;
    response.noteLoanAmount = totalloanamount;
    response.firstRatio = firstRatio;
    response.secondRatio = secondRatio;
    response.ltv = ltvresponse.ltv;
    response.cltv = ltvresponse.cltv;
    response.hcltv = ltvresponse.hcltv;
    response.firstMortgagePI = firstMortgagePI;
    response.monthlyMiAmount = monthlyMiAmount;
    response.monthlyMiFactor = monthlyMiFactor;
    response.piti = loanInfo.proposedCombinedHousingExpenses;

    return response;
  };
  calculateLoanGvtFundingFactors = (loanInfo: LoanInfoRequestGvtFundingFactors): LoanInfoResponse2 => {
    let firstRatio = 0;
    let secondRatio = 0;
    if (loanInfo.mipffFinancingCalcOption === null || loanInfo.mipffFinancingCalcOption === 0) {
      loanInfo.upfrontMiType = 'financed';
    } else {
      loanInfo.upfrontMiType = 'paidInCash';
    }
    const response = new LoanInfoResponse();
    let ltvresponse = new LtvCltvHcltvResponse();
    const baseLoan = roundToNearestValue(
      this._calculateBaseLoanAmount(
        loanInfo.downPaymentPercent,
        loanInfo.downPayment,
        loanInfo.salesPrice,
        loanInfo.appraisalValue,
        loanInfo.dpFactor,
        loanInfo.baseLoan,
      ),
    );

    ltvresponse = this._calculateLTVCLTVHCLTV(
      loanInfo.mortgageType,
      baseLoan,
      loanInfo.appraisalValue,
      loanInfo.salesPrice,
      loanInfo.subordinateFinanceAmount,
      loanInfo.totalLoanAmount,
      loanInfo.loanPurpose,
      loanInfo.hellocCreditLimit,
    );
    const firstMortgagePI = formatNumber(
      this._calculatePI(loanInfo.totalLoanAmount, loanInfo.interestRate, loanInfo.loanTerm, !loanInfo.loanTerm || loanInfo.loanTerm <= 0),
      2,
    );

    loanInfo.proposedCombinedHousingExpenses = formatNumber(
      checkIsNAN(loanInfo.proposedCombinedHousingExpenses) + checkIsNAN(firstMortgagePI) + checkIsNAN(loanInfo.monthlyMiAmount),
      2,
    );
    if (loanInfo.occupancyType == 2) {
      loanInfo.PresentCombinedHousingExpenses = formatNumber(loanInfo.PresentCombinedHousingExpenses, 2);
    }

    if (loanInfo.grossMonthlyIncome > 0) {
      if (loanInfo.occupancyType === 1) {
        if (loanInfo.loanPurpose == '1' && (loanInfo.mortgageType == 2 || loanInfo.mortgageType == 4)) {
          firstRatio = formatNumber(
            this._calculateFirstRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.grossMonthlyIncome, loanInfo.monthlyMCC),
            2,
          );
          secondRatio = formatNumber(
            this._calculateSecondRatio(
              loanInfo.proposedCombinedHousingExpenses,
              loanInfo.totalMonthlyPayment,
              loanInfo.grossMonthlyIncome,
              loanInfo.monthlyMCC,
            ),
            2,
          );
        } else {
          firstRatio = formatNumber(this._calculateFirstRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.grossMonthlyIncome, 0), 2);
          secondRatio = formatNumber(
            this._calculateSecondRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.totalMonthlyPayment, loanInfo.grossMonthlyIncome, 0),
            2,
          );
        }
      } else if (loanInfo.occupancyType === 2) {
        firstRatio = formatNumber(this._calculateFirstRatio(loanInfo.PresentCombinedHousingExpenses, loanInfo.grossMonthlyIncome, 0), 2);
        let PresentAndProposedCombinedHousingExpenses = 0;
        PresentAndProposedCombinedHousingExpenses = loanInfo.PresentCombinedHousingExpenses + loanInfo.proposedCombinedHousingExpenses;
        secondRatio = formatNumber(
          this._calculateSecondRatio(PresentAndProposedCombinedHousingExpenses, loanInfo.totalMonthlyPayment, loanInfo.grossMonthlyIncome, 0),
          2,
        );
      } else {
        firstRatio = formatNumber(this._calculateFirstRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.grossMonthlyIncome, 0), 2);
        secondRatio = formatNumber(
          this._calculateSecondRatio(loanInfo.proposedCombinedHousingExpenses, loanInfo.totalMonthlyPayment, loanInfo.grossMonthlyIncome, 0),
          2,
        );
      }
    }

    response.baseLoan = baseLoan;
    response.firstRatio = firstRatio;
    response.secondRatio = secondRatio;
    response.ltv = ltvresponse.ltv;
    response.cltv = ltvresponse.cltv;
    response.hcltv = ltvresponse.hcltv;
    response.firstMortgagePI = firstMortgagePI;
    response.piti = loanInfo.proposedCombinedHousingExpenses;

    return response;
  };

  private readonly _calculateMonthlyMiFactor = (
    isRefinance: boolean,
    mortgageType: number,
    ltv: number,
    baseLoan: number,
    loanTerm: number,
    fhaCaseNoAssigned: number,
    monthlyMiFactor: number,
    loanProgramCode: string,
  ): number => {
    let mipFactor = 0.8;
    let fhaCaseNumber: number = fhaCaseNoAssigned;
    const fhaBaseLoanAmountCutoff = fhaCaseNumber > 14 ? 726200 : 625500;
    const isHud184 = loanProgramCode?.toLocaleLowerCase().includes('hud184');

    if (fhaCaseNoAssigned > 11 && fhaCaseNoAssigned < 15) {
      fhaCaseNumber = 11;
    }
    if (mortgageType === MortgageType.fha) {
      if (isHud184) {
        return 0.0;
      }

      //  fhaCaseNoAssigned details not given for other values
      if (fhaCaseNumber != 10 && fhaCaseNumber != 11 && fhaCaseNumber != 15) {
        return mipFactor;
      }
      const term = loanTerm / 12;
      if (baseLoan <= fhaBaseLoanAmountCutoff && term > 15) {
        mipFactor = this.GetFHAFactorMatrixForCase1().filter((x) => x.ltvGreaterThanLimit == ltv > 95 && x.fhaCaseNoAssigned == fhaCaseNumber)[0]
          .mipFactor;
      }
      if (baseLoan > fhaBaseLoanAmountCutoff && term > 15) {
        mipFactor = this.GetFHAFactorMatrixForCase2().filter((x) => x.ltvGreaterThanLimit == ltv > 95 && x.fhaCaseNoAssigned == fhaCaseNumber)[0]
          .mipFactor;
      }

      if (baseLoan <= fhaBaseLoanAmountCutoff && term <= 15) {
        mipFactor = this.GetFHAFactorMatrixForCase4().filter((x) => x.ltvGreaterThanLimit == ltv > 90 && x.fhaCaseNoAssigned == fhaCaseNumber)[0]
          .mipFactor;
      }
      if (baseLoan > fhaBaseLoanAmountCutoff && term <= 15) {
        if (ltv > 90) {
          mipFactor = this.GetFHAFactorMatrixForCase3().filter((x) => x.ltvGreaterThanLimit == ltv > 90 && x.fhaCaseNoAssigned == fhaCaseNumber)[0]
            .mipFactor;
        } else {
          mipFactor = this.GetFHAFactorMatrixForCase4().filter((x) => x.ltvGreaterThanLimit == ltv > 78 && x.fhaCaseNoAssigned == fhaCaseNumber)[0]
            .mipFactor;
        }
      }
    } else if (mortgageType == 4) {
      mipFactor = this.GetRHSFactorMatrix().filter((x) => x.isRefinance == isRefinance && x.usdaCaseNoAssigned == fhaCaseNumber)[0]?.mipFactor;
    } else {
      mipFactor = monthlyMiFactor;
    }
    return mipFactor;
  };

  private readonly _calculateLTVCLTVHCLTV = (
    mortagageType: number,
    baseLoan: number,
    appraisalValue: number,
    purchsePrice: number,
    subordinateFinanceAmount: number,
    noteLoanAmount: number,
    loanPurpose: string,
    helocCreditLimit: number,
  ): LtvCltvHcltvResponse => {
    const response = new LtvCltvHcltvResponse();
    let lessValue = 0;
    if (appraisalValue == 0 && purchsePrice == 0) {
      response.ltv = 0;
      response.cltv = 0;
      response.hcltv = 0;
      return response;
    }
    if (!appraisalValue || appraisalValue == 0.0) {
      lessValue = purchsePrice;
    } else if (appraisalValue == purchsePrice) {
      lessValue = appraisalValue;
    } else if (appraisalValue > purchsePrice) {
      lessValue = purchsePrice;
    } else {
      lessValue = appraisalValue;
    }

    if (
      !!mortagageType &&
      (mortagageType == 1 || mortagageType == 2 || mortagageType == 5 || mortagageType == 6 || mortagageType == 7 || mortagageType == 8) &&
      lessValue != 0
    ) {
      if (!!loanPurpose && (loanPurpose == '1' || loanPurpose == '3' || loanPurpose == '4')) {
        const ltv = (baseLoan / lessValue) * 100;
        const cltv = ((baseLoan + Number(subordinateFinanceAmount)) / lessValue) * 100;
        response.ltv = ltv;
        response.cltv = cltv;
      } else if (loanPurpose == '8') {
        if (appraisalValue == null || appraisalValue == 0.0) {
          appraisalValue = purchsePrice;
        }
        const ltv = (baseLoan / appraisalValue) * 100;
        const cltv = ((baseLoan + Number(subordinateFinanceAmount)) / appraisalValue) * 100;
        response.ltv = ltv;
        response.cltv = cltv;
      } else if (loanPurpose == '2') {
        const ltv = (baseLoan / appraisalValue) * 100;
        const cltv = ((baseLoan + Number(subordinateFinanceAmount)) / appraisalValue) * 100;
        response.ltv = ltv;
        response.cltv = cltv;
      } else if (loanPurpose == '5') {
        const ltv = (baseLoan / appraisalValue) * 100;
        const cltv = ((baseLoan + noteLoanAmount) / appraisalValue) * 100;
        response.ltv = ltv;
        response.cltv = cltv;
      }
    } else if (!!mortagageType && mortagageType == 3 && lessValue != 0) {
      if (!!loanPurpose && (loanPurpose == '1' || loanPurpose == '3' || loanPurpose == '4')) {
        const ltv = (noteLoanAmount / lessValue) * 100;
        const cltv = ((noteLoanAmount + Number(subordinateFinanceAmount)) / lessValue) * 100;
        response.ltv = ltv;
        response.cltv = cltv;
      } else if (loanPurpose == '8' || loanPurpose == '2') {
        if (appraisalValue == null || appraisalValue == 0.0) {
          appraisalValue = purchsePrice;
        }
        const ltv = (noteLoanAmount / appraisalValue) * 100;
        const cltv = ((noteLoanAmount + Number(subordinateFinanceAmount)) / appraisalValue) * 100;
        response.ltv = ltv;
        response.cltv = cltv;
      } else if (loanPurpose == '5') {
        const ltv = (baseLoan / appraisalValue) * 100;
        const cltv = ((baseLoan + noteLoanAmount) / appraisalValue) * 100;
        response.ltv = ltv;
        response.cltv = cltv;
      }
    } else if (!!mortagageType && mortagageType == 4) {
      if (appraisalValue == null || appraisalValue == 0.0) {
        appraisalValue = purchsePrice;
      }
      const ltv = (noteLoanAmount / appraisalValue) * 100;
      const cltv = ((noteLoanAmount + Number(subordinateFinanceAmount)) / appraisalValue) * 100;
      response.ltv = ltv;
      response.cltv = cltv;
    }
    const hcltvLoanAmount: number = mortagageType == 4 || mortagageType == 3 ? noteLoanAmount : baseLoan;
    response.hcltv =
      loanPurpose == '2' || (!!mortagageType && mortagageType == 4)
        ? this._calculateHCLTV(helocCreditLimit, hcltvLoanAmount, Number(appraisalValue))
        : this._calculateHCLTV(helocCreditLimit, hcltvLoanAmount, Number(lessValue));

    if (mortagageType == 3) {
      response.ltv = Math.ceil(response.ltv);
      response.cltv = Math.ceil(response.cltv);
      response.hcltv = Math.ceil(response.hcltv);
    } else {
      response.ltv = formatNumber(response.ltv, 2);
      response.cltv = formatNumber(response.cltv, 2);
      response.hcltv = formatNumber(response.hcltv, 2);
    }
    return response;
  };

  private readonly _calculateHCLTV = (helocCreditLimit: number, loanAmount: number, appraisalValue: number): number => {
    let hcltv = 0;
    let helocCreditAmount = 0;
    let baseLoanAmount = 0;
    let appraisedValue = 0;
    helocCreditAmount = checkIsNAN(helocCreditLimit);
    baseLoanAmount = checkIsNAN(Number(loanAmount));
    appraisedValue = checkIsNAN(Number(appraisalValue));
    if (helocCreditAmount != null && helocCreditAmount >= 0 && !!appraisedValue && appraisedValue != 0) {
      hcltv = ((helocCreditAmount + baseLoanAmount) / appraisedValue) * 100;
      return hcltv;
    }
    return hcltv;
  };

  private readonly _calculateNoteLoanAmount = (
    upfrontMiType: string,
    mortagageType: number,
    baseLoanAmount: number,
    upfrontMiAmount: number,
    mipPaidInCash: number,
  ): number => {
    let totalLoanAmount: number = baseLoanAmount ? Number(baseLoanAmount) : 0;
    if (upfrontMiType == 'financed' && mortagageType != 0) {
      totalLoanAmount = (baseLoanAmount ? Number(baseLoanAmount) : 0) + (Number(upfrontMiAmount) - mipPaidInCash);
    }
    return totalLoanAmount;
  };

  private readonly _calculateMIPaidInCash = (upfrontMiType: string, upfrontMiAmount: number): number => {
    let mipPaidInCash = upfrontMiAmount - Math.floor(upfrontMiAmount);

    if (upfrontMiType === 'financed') {
      mipPaidInCash = parseFloat(mipPaidInCash.toFixed(2));
    } else {
      mipPaidInCash = parseFloat(upfrontMiAmount.toFixed(2));
    }

    return mipPaidInCash;
  };

  _calculateUpfrontMiFactor = (
    isRefinance: boolean,
    mortgageType: MortgageType,
    fundingFeeType: FundingFeeType,
    loanProgramCode: string,
    salesPrice: number,
    baseLoan: number,
    upfrontMifactor: number,
    fhaCaseNoAssigned: number,
  ): number => {
    const isRefinanceType = isRefinance;
    const isHud184 = loanProgramCode?.toLocaleLowerCase().includes('hud184');
    let upfrontMiFactor = 0;

    if (mortgageType === MortgageType.va) {
      if (isRefinanceType) {
        if (fundingFeeType === FundingFeeType.regularMilitaryFirstUse || fundingFeeType === FundingFeeType.reservesOrGuardFirstUse) {
          upfrontMiFactor = 2.15;
        } else if (fundingFeeType === FundingFeeType.regularMilitarySubequentUse || fundingFeeType === FundingFeeType.reservesOrGuardSubsequentUse) {
          upfrontMiFactor = 3.3;
        } else if (fundingFeeType === FundingFeeType.interestRateReductionRefi) {
          upfrontMiFactor = 0.5;
        }
      } else {
        const downPaymentPercentage = formatNumber((1 - baseLoan / salesPrice) * 100, 3);

        if (fundingFeeType === FundingFeeType.manufacturedHome) {
          upfrontMiFactor = 1.0;
        } else if (fundingFeeType === FundingFeeType.interestRateReductionRefi || fundingFeeType === FundingFeeType.loanAssumption) {
          upfrontMiFactor = 0.5;
        } else if (downPaymentPercentage < 5.0) {
          if (fundingFeeType === FundingFeeType.regularMilitaryFirstUse || fundingFeeType === FundingFeeType.reservesOrGuardFirstUse) {
            upfrontMiFactor = 2.15;
          } else if (
            fundingFeeType === FundingFeeType.regularMilitarySubequentUse ||
            fundingFeeType === FundingFeeType.reservesOrGuardSubsequentUse
          ) {
            upfrontMiFactor = 3.3;
          }
        } else if (downPaymentPercentage >= 5.0 && downPaymentPercentage < 10.0) {
          if (
            fundingFeeType === FundingFeeType.regularMilitaryFirstUse ||
            fundingFeeType === FundingFeeType.regularMilitarySubequentUse ||
            fundingFeeType === FundingFeeType.reservesOrGuardFirstUse ||
            fundingFeeType === FundingFeeType.reservesOrGuardSubsequentUse
          ) {
            upfrontMiFactor = 1.5;
          }
        } else if (downPaymentPercentage >= 10.0) {
          if (
            fundingFeeType === FundingFeeType.regularMilitaryFirstUse ||
            fundingFeeType === FundingFeeType.regularMilitarySubequentUse ||
            fundingFeeType === FundingFeeType.reservesOrGuardFirstUse ||
            fundingFeeType === FundingFeeType.reservesOrGuardSubsequentUse
          ) {
            upfrontMiFactor = 1.25;
          }
        }
      }
    } else if (mortgageType === MortgageType.fha) {
      if (isHud184) {
        upfrontMiFactor = 1.0;
      } else {
        upfrontMiFactor = 1.75;
      }
    } else if (mortgageType === MortgageType.usda) {
      upfrontMiFactor = this._getRHSFactorMatrix().filter((x) => x.isRefinance === isRefinanceType && x.usdaCaseNoAssigned === fhaCaseNoAssigned)[0]
        ?.upfrontMiFactor;
    } else {
      upfrontMiFactor = upfrontMifactor;
    }

    return upfrontMiFactor;
  };

  private readonly _calculateFirstRatio = (proposedCombinedHousingExpenses: number, grossMonthlyIncome: number, monthlyMCC: number): number => {
    const firstRatio = ((proposedCombinedHousingExpenses - monthlyMCC) / grossMonthlyIncome) * 100;
    return firstRatio;
  };

  private readonly _calculateSecondRatio = (
    proposedCombinedHousingExpenses: number,
    totalMonthlyPayment: number,
    grossMonthlyIncome: number,
    monthlyMCC: number,
  ): number => {
    const secondRatio = ((proposedCombinedHousingExpenses + totalMonthlyPayment - monthlyMCC) / grossMonthlyIncome) * 100;
    return secondRatio;
  };

  private readonly _calculateBaseLoanAmount = (
    downpaypercent: number,
    downpayamount: number,
    salesprice: number,
    appraisalValue: number,
    dpfactor: string,
    baseLoanAmount: number,
  ): number => {
    let baseLoan = 0;
    if (dpfactor == '' || dpfactor == null || dpfactor == undefined) {
      dpfactor = DpFactorType.select;
    }
    if (downpaypercent == undefined || downpaypercent == null || downpaypercent.toString() == '0' || downpaypercent.toString() == '0.000.00') {
      downpaypercent = 0.0;
    }
    if (
      downpayamount == undefined ||
      downpayamount == null ||
      downpayamount.toString() == '0' ||
      downpayamount.toString() == '0.00.0' ||
      downpayamount.toString() == '0.000.0'
    ) {
      downpayamount = 0;
    }
    if (downpaypercent > 100) {
      downpaypercent = 100;
    }
    if (downpayamount > salesprice) {
      downpayamount = salesprice;
    }

    const priceValue = !!appraisalValue && appraisalValue < salesprice ? appraisalValue : salesprice;
    if (!!salesprice && !!appraisalValue && dpfactor === DpFactorType.downpaymentPercentage) {
      const downPaymentPercent = new Decimal(downpaypercent);
      const price = new Decimal(priceValue);
      const hundred = new Decimal(100);
      baseLoan = price.mul(Decimal.sub(1, downPaymentPercent.div(hundred))).toNumber();
    } else if (!!priceValue && dpfactor === DpFactorType.downpaymentAmount && priceValue >= downpayamount) {
      baseLoan = priceValue - downpayamount;
    } else if ((salesprice == undefined || salesprice == null || salesprice.toString() == '') && !!dpfactor && dpfactor !== DpFactorType.select) {
      baseLoan = 0;
    } else if (dpfactor === DpFactorType.select) {
      baseLoan = baseLoanAmount;
    }
    return baseLoan;
  };

  // for RHS/USDA
  private readonly _getRHSFactorMatrix = (): RHSFactorMatrix[] => {
    return [
      {
        usdaCaseNoAssigned: 0,
        mipFactor: 0,
        upfrontMiFactor: 3.5,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 0,
        mipFactor: 0,
        upfrontMiFactor: 1.0,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 1,
        mipFactor: 0.3,
        upfrontMiFactor: 2.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 1,
        mipFactor: 0.3,
        upfrontMiFactor: 1.0,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 2,
        mipFactor: 0.3,
        upfrontMiFactor: 2.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 2,
        mipFactor: 0.3,
        upfrontMiFactor: 1.5,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 3,
        mipFactor: 0.4,
        upfrontMiFactor: 2.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 3,
        mipFactor: 0.4,
        upfrontMiFactor: 2.0,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 4,
        mipFactor: 0.5,
        upfrontMiFactor: 2.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 4,
        mipFactor: 0.5,
        upfrontMiFactor: 2.0,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 5,
        mipFactor: 0.5,
        upfrontMiFactor: 2.75,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 5,
        mipFactor: 0.5,
        upfrontMiFactor: 2.75,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 6,
        mipFactor: 0.35,
        upfrontMiFactor: 1.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 6,
        mipFactor: 0.35,
        upfrontMiFactor: 1.0,
        isRefinance: true,
      },
    ];
  };

  private readonly _calculateUpfrontAmount = (upfrontMiFactor: number, baseAmount: number, mortgageType: number): number => {
    if (mortgageType != 4 && mortgageType != 0) {
      const x: number = (baseAmount ? Number(baseAmount) : 0) * (upfrontMiFactor ? Number(upfrontMiFactor) / 100 : 0);
      return x;
    } else if (mortgageType == 4) {
      const x: number = (baseAmount ? Number(baseAmount) : 0) / (upfrontMiFactor ? 1 - Number(upfrontMiFactor / 100) : 0);
      const upfrontMiAmount = x - baseAmount;
      return upfrontMiAmount;
      //  else return 0 so the rest of calcuations can occur
    } else {
      return 0;
    }
  };

  private readonly _calculateMIAmount = (
    principleAndInterest: number,
    totalloanamount: number,
    interestrate: number,
    factorPerMonth: number,
    upfrontmifactor: number,
    mortgageType: number,
    baseloan: number,
  ): number => {
    if (mortgageType != 3 && mortgageType != 5 && mortgageType != 6) {
      const pi: number = principleAndInterest ? Number(principleAndInterest) : 0;
      const totalLoanAmount: number = totalloanamount ? Number(totalloanamount) : 0;
      const interestRate: number = (interestrate ? Number(interestrate) : 0) / 1200;
      const annualFee: number = factorPerMonth ? Number(factorPerMonth) / 100 : 0;
      const upfrontMiFactor: number = upfrontmifactor ? Number(upfrontmifactor) / 100 : 0;
      let calculatedInterest = 0;
      let principleReduced = 0;
      let balance: number = totalLoanAmount;
      let sum: number = totalLoanAmount;
      for (let i = 1; i <= 11; i++) {
        calculatedInterest = balance * interestRate;
        principleReduced = pi - calculatedInterest;
        balance = balance - principleReduced;
        sum = sum + balance;
      }
      const remainingBalance: number = sum * annualFee;
      let step3Amount: number = remainingBalance / 12;

      if (mortgageType != 4 && mortgageType != 0 && baseloan != totalloanamount) {
        step3Amount = step3Amount / (1 + upfrontMiFactor);
      }
      step3Amount = step3Amount / 12;
      return step3Amount;
    } else if (mortgageType == 6) {
      return formatNumber((baseloan * factorPerMonth) / (100 * 12), 2);
    } else {
      return formatNumber((totalloanamount * factorPerMonth) / (100 * 12), 2);
    }
  };

  private readonly _calculatePI = (noteLoanAmount: number, interestRate: number, loanTerm: number, isInterestOnly?: boolean): number => {
    if (interestRate == null) {
      return null;
    }
    const r = interestRate / 100 / 12;
    const l = noteLoanAmount;
    const n = loanTerm;
    if (r == 0 && n == 0) {
      return 0;
    }
    if (r == 0) {
      return formatNumber(l / n, 2);
    }
    const z = Math.pow(1 + r, n);
    if (isInterestOnly) {
      return formatNumber(r * l, 2);
    }
    return formatNumber(((r * z) / (z - 1)) * l, 2);
  };

  //term > 15 and base loan > 625000
  GetFHAFactorMatrixForCase2 = (): FHAFactorMatrix[] => {
    const fhaMatrix = [
      {
        fhaCaseNoAssigned: 11,
        mipFactor: 1,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 10,
        mipFactor: 1.5,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 10,
        mipFactor: 1.55,
        ltvGreaterThanLimit: true,
      },
      {
        fhaCaseNoAssigned: 11,
        mipFactor: 1.05,
        ltvGreaterThanLimit: true,
      },
      {
        fhaCaseNoAssigned: 15,
        mipFactor: 0.7,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 15,
        mipFactor: 0.75,
        ltvGreaterThanLimit: true,
      },
    ];
    return fhaMatrix;
  };

  //term > 15 and base loan <= 625000
  GetFHAFactorMatrixForCase1 = (): FHAFactorMatrix[] => {
    const fhaMatrix = [
      {
        fhaCaseNoAssigned: 11,
        mipFactor: 0.8,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 10,
        mipFactor: 1.3,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 10,
        mipFactor: 1.35,
        ltvGreaterThanLimit: true,
      },
      {
        fhaCaseNoAssigned: 11,
        mipFactor: 0.85,
        ltvGreaterThanLimit: true,
      },
      {
        fhaCaseNoAssigned: 15,
        mipFactor: 0.5,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 15,
        mipFactor: 0.55,
        ltvGreaterThanLimit: true,
      },
    ];
    return fhaMatrix;
  };

  //term < 15 && base loan > 625000 && ltv 90 & 78 & 78- 90
  GetFHAFactorMatrixForCase3 = (): FHAFactorMatrix[] => {
    const fhaMatrix = [
      {
        fhaCaseNoAssigned: 11,
        mipFactor: 0.95,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 10,
        mipFactor: 0.95,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 11,
        mipFactor: 0.95,
        ltvGreaterThanLimit: true,
      },
      {
        fhaCaseNoAssigned: 10,
        mipFactor: 0.95,
        ltvGreaterThanLimit: true,
      },
      {
        fhaCaseNoAssigned: 15,
        mipFactor: 0.65,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 15,
        mipFactor: 0.65,
        ltvGreaterThanLimit: true,
      },
    ];
    return fhaMatrix;
  };

  //term < 15 && base loan > 625000 && ltv > 90
  GetFHAFactorMatrixForCase4 = (): FHAFactorMatrix[] => {
    const fhaMatrix = [
      {
        fhaCaseNoAssigned: 11,
        mipFactor: 0.45,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 10,
        mipFactor: 0.45,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 11,
        mipFactor: 0.7,
        ltvGreaterThanLimit: true,
      },
      {
        fhaCaseNoAssigned: 10,
        mipFactor: 0.7,
        ltvGreaterThanLimit: true,
      },
      {
        fhaCaseNoAssigned: 15,
        mipFactor: 0.15,
        ltvGreaterThanLimit: false,
      },
      {
        fhaCaseNoAssigned: 15,
        mipFactor: 0.4,
        ltvGreaterThanLimit: true,
      },
    ];
    return fhaMatrix;
  };

  // for RHS/USDA
  GetRHSFactorMatrix = (): RHSFactorMatrix[] => {
    const rhaMatrix = [
      {
        usdaCaseNoAssigned: 0,
        mipFactor: 0,
        upfrontMiFactor: 3.5,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 0,
        mipFactor: 0,
        upfrontMiFactor: 1.0,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 1,
        mipFactor: 0.3,
        upfrontMiFactor: 2.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 1,
        mipFactor: 0.3,
        upfrontMiFactor: 1.0,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 2,
        mipFactor: 0.3,
        upfrontMiFactor: 2.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 2,
        mipFactor: 0.3,
        upfrontMiFactor: 1.5,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 3,
        mipFactor: 0.4,
        upfrontMiFactor: 2.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 3,
        mipFactor: 0.4,
        upfrontMiFactor: 2.0,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 4,
        mipFactor: 0.5,
        upfrontMiFactor: 2.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 4,
        mipFactor: 0.5,
        upfrontMiFactor: 2.0,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 5,
        mipFactor: 0.5,
        upfrontMiFactor: 2.75,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 5,
        mipFactor: 0.5,
        upfrontMiFactor: 2.75,
        isRefinance: true,
      },
      {
        usdaCaseNoAssigned: 6,
        mipFactor: 0.35,
        upfrontMiFactor: 1.0,
        isRefinance: false,
      },
      {
        usdaCaseNoAssigned: 6,
        mipFactor: 0.35,
        upfrontMiFactor: 1.0,
        isRefinance: true,
      },
    ];
    return rhaMatrix;
  };

  calculatePI = (subOrdinateFinance: SubordinateFinanceModel, totalRelatedLoanAmount: number): SubordinateFinanceModel => {
    //  Clear 1 Code
    let calPi = 0;
    if (subOrdinateFinance.subFiIntRate && !subOrdinateFinance.subFiIntRate) {
      subOrdinateFinance.subFiIntRate = parseInt(this._checkForInterestValue(subOrdinateFinance.subFiIntRate.toString()));
    }

    // Clear 1 retains the calculatedPi if subFiIntRate IS null but calculatedPi IS NOT null.
    const keepCurrentCalculatedPi = !subOrdinateFinance.subFiIntRate && subOrdinateFinance.calculatedPi;

    // This prevents this._calculatePi() from setting calculatedPi to null for the above scenario.
    if (keepCurrentCalculatedPi) {
      calPi = subOrdinateFinance.calculatedPi;
    } else {
      calPi = this._calculatePI(
        totalRelatedLoanAmount,
        subOrdinateFinance.subFiIntRate,
        subOrdinateFinance.subFiTerm,
        subOrdinateFinance.subFiIntOnly,
      );
    }

    subOrdinateFinance.calculatedPi = calPi;
    subOrdinateFinance.subFiBaseLoan = totalRelatedLoanAmount;
    return subOrdinateFinance;
  };

  private _checkForInterestValue(item: string): string {
    let i: string[] = [];
    i = item.split('.');
    if (!!i[0] && Number(i[0]) > 100) {
      return '0.00';
    } else if (Number(i[0]) == 100 && Number(i[1]) > 0) {
      return '0.00';
    }
    return item;
  }
}
